import { supabase } from './supabase'
import {
  clampCropIn,
  cropBoxIn,
  initialCropIn,
  maxZoomIn,
  panCropIn,
  scaleIn,
  zoomCropIn,
  type Crop,
  type Frame,
} from './photoCrop'

/**
 * The picture on your own business card (132). David, 6 Oct.
 *
 * ── SELF-ENTERED, SELF-READ ─────────────────────────────────────────────────
 *
 * Like the phone (083): yours to choose, never synced from BambooHR, and only
 * you can read it back. The card is the one place it shows, and you are the
 * one showing the card. A face beside colleagues' posts is a different
 * question (a tenant-wide read of personal data) and is NOT answered here —
 * the Storage policies are self-only on purpose. See the 132 header.
 *
 * ── A 512px SQUARE JPEG, ALWAYS ─────────────────────────────────────────────
 *
 * The card draws the photo in a 100px circle, so 512px is ~5x — sharp on any
 * phone, and small enough (~40–80 KB) that the downloaded .vcf carrying it
 * stays a contact rather than an attachment. WHICH square is the person's
 * choice, in PhotoCropDialog (drag to move, pinch / scroll / slider to zoom);
 * the arithmetic is below, pure, so the suite can prove it.
 *
 * The re-encode through a canvas is also what strips the EXIF, for photos.ts's
 * reason (a selfie at home carries the coordinates of home). Same caveat too:
 * it is client-side, so a courtesy rather than a control. Orientation is baked
 * into the pixels first with `imageOrientation: 'from-image'`, or every
 * portrait photo comes out on its side.
 */

export const PROFILE_PHOTO_BUCKET = 'profile-photo'
/** The edge of the stored square. See the header. */
export const PROFILE_PHOTO_EDGE = 512
const QUALITY = 0.85

export type MyPhoto = {
  /** `<tenant>/<person>` — where an upload must go. From the database, so it
   *  is the same tenant set_my_photo checks against. */
  folder: string
  /** The live photo's path, or null when there is none. */
  path: string | null
}

export async function loadMyPhoto(): Promise<MyPhoto> {
  const { data, error } = await supabase
    .rpc('my_photo')
    .single<{ folder: string; storage_path: string | null }>()
  if (error) throw error
  return { folder: data.folder, path: data.storage_path }
}

/**
 * Decode a picked file, with its EXIF rotation baked into the pixels. Rejects
 * with 'not-an-image' for anything a browser cannot draw (HEIC on Chrome lands
 * here), so the card dialog can say "pick a JPEG" before any cropper opens.
 */
export async function decodePhoto(file: File): Promise<ImageBitmap> {
  if (!/^image\/(jpeg|png|webp|heic|heif)$/.test(file.type) && file.type !== '') {
    throw new Error('not-an-image')
  }
  try {
    // from-image: stripping the EXIF also strips the Orientation tag, so
    // without this every portrait photo comes out on its side.
    return await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    throw new Error('not-an-image')
  }
}

/**
 * Draw the chosen square into a fresh JPEG of at most 512px. The re-encode is
 * what strips the EXIF — a new file, no metadata. Upscaling is never done: a
 * crop smaller than 512px is stored at its own size rather than blurred up.
 */
export async function renderCrop(bitmap: ImageBitmap, rect: CropRect): Promise<Blob> {
  const edge = Math.max(1, Math.min(PROFILE_PHOTO_EDGE, Math.round(rect.side)))
  const canvas = document.createElement('canvas')
  canvas.width = edge
  canvas.height = edge
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('not-an-image')
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(bitmap, rect.sx, rect.sy, rect.side, rect.side, 0, 0, edge, edge)
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY))
  if (!blob) throw new Error('not-an-image')
  return blob
}

/* ── THE CROP, AS ARITHMETIC (David, 6 Oct: "a modal to crop and zoom") ──────
 *
 * Pure functions, so the suite can prove them without a canvas. The model is
 * one number and one point:
 *
 *   zoom   1 = the photo's SHORT side exactly fills the square (nothing but
 *          photo is ever in the frame — there is no letterbox to save);
 *          up to 4x, or less for a small photo (maxZoom).
 *   cx,cy  the point of the photo, in the photo's own pixels, at the centre
 *          of the square.
 *
 * Every move goes through clampCrop, which keeps the square inside the photo.
 * So "drag past the edge" stops at the edge instead of showing grey, and the
 * saved file is always all picture.
 */

export type { Crop } from './photoCrop'
export type CropRect = { sx: number; sy: number; side: number }

/* Since FED-66 (12 Oct) these are the square case of lib/photoCrop.ts, which
 * holds the arithmetic for any frame — the feed's crop uses the same model.
 * The names and signatures are kept so the card's callers and the suite did
 * not move; a square `view` is a frame `view` x `view`. */
const sq = (view: number): Frame => ({ w: view, h: view })

/** A crop never goes below 128 photo pixels across — past that it is a blur
 *  of somebody's nose, and 4x is plenty for a face in a group shot. Frame-
 *  independent for a square, so any square stands in. */
export function maxZoom(w: number, h: number): number {
  return maxZoomIn(sq(1), w, h)
}

/** Screen pixels per photo pixel, for a square `view` px across. */
export function cropScale(view: number, w: number, h: number, zoom: number): number {
  return scaleIn(sq(view), w, h, zoom)
}

export function clampCrop(view: number, w: number, h: number, c: Crop): Crop {
  return clampCropIn(sq(view), w, h, c)
}

/** Zoom 1, centred — the same picture 132 used to take without asking. */
export function initialCrop(w: number, h: number): Crop {
  return initialCropIn(w, h)
}

/** A drag of (dx, dy) SCREEN pixels moves the photo with the finger. */
export function panCrop(view: number, w: number, h: number, c: Crop, dx: number, dy: number): Crop {
  return panCropIn(sq(view), w, h, c, dx, dy)
}

/**
 * Zoom to `zoom`, keeping the photo point under (ax, ay) — screen pixels from
 * the square's centre — where it is. That is what makes a pinch zoom INTO the
 * face between your fingers rather than towards the middle of the frame.
 */
export function zoomCrop(view: number, w: number, h: number, c: Crop, zoom: number, ax = 0, ay = 0): Crop {
  return zoomCropIn(sq(view), w, h, c, zoom, ax, ay)
}

/** The square, in photo pixels, that the frame is showing. */
export function cropRect(view: number, w: number, h: number, c: Crop): CropRect {
  const b = cropBoxIn(sq(view), w, h, c)
  return { sx: b.sx, sy: b.sy, side: b.sw }
}

/**
 * Upload, then point the row at it, then remove the file it replaced.
 *
 * In that order on purpose. Uploading first means the row never names bytes
 * that are not there (set_my_photo refuses that anyway). Deleting LAST means a
 * failure anywhere leaves the old photo showing rather than none; the worst
 * case is an orphan file in your own folder, which only you can read.
 *
 * The file name is fresh every time rather than an upsert on one name, so a
 * signed URL can never serve yesterday's face out of a cache.
 */
export async function saveProfilePhoto(folder: string, photo: Blob): Promise<string> {
  const path = `${folder}/${crypto.randomUUID()}.jpg`
  const up = await supabase.storage
    .from(PROFILE_PHOTO_BUCKET)
    .upload(path, photo, { contentType: 'image/jpeg', upsert: false })
  if (up.error) throw up.error

  const { data: replaced, error } = await supabase.rpc('set_my_photo', { p_path: path })
  if (error) {
    // The row did not move, so the new bytes are an orphan. Best effort.
    void supabase.storage.from(PROFILE_PHOTO_BUCKET).remove([path])
    throw error
  }
  if (typeof replaced === 'string' && replaced) {
    void supabase.storage.from(PROFILE_PHOTO_BUCKET).remove([replaced])
  }
  return path
}

/** Clear the row, then delete the bytes it pointed at. */
export async function removeProfilePhoto(): Promise<void> {
  const { data: replaced, error } = await supabase.rpc('set_my_photo', { p_path: null })
  if (error) throw error
  if (typeof replaced === 'string' && replaced) {
    await supabase.storage.from(PROFILE_PHOTO_BUCKET).remove([replaced])
  }
}

/**
 * The live photo as a data URL — one fetch that serves BOTH the face and the
 * .vcf. A data URL rather than a signed URL because the vCard needs the bytes
 * anyway, and because a signed URL expires (an hour) while the dialog may sit
 * open on a phone for longer than that at a trade show.
 */
export async function photoDataUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from(PROFILE_PHOTO_BUCKET).download(path)
  if (error || !data) return null
  return await new Promise<string | null>((resolve) => {
    const r = new FileReader()
    r.onload = () => resolve(typeof r.result === 'string' ? r.result : null)
    r.onerror = () => resolve(null)
    r.readAsDataURL(data)
  })
}

/** The base64 half of a JPEG data URL, for the vCard's PHOTO line. Anything
 *  that is not a JPEG data URL gives null — vCard 3.0 says TYPE=JPEG and a
 *  PNG under that label imports as a blank square on some Androids. */
export function jpegBase64(dataUrl: string | null): string | null {
  const m = dataUrl?.match(/^data:image\/jpe?g;base64,([A-Za-z0-9+/=]+)$/)
  return m ? m[1] : null
}
